import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { TextChain } from '@/api/types/stowed-element'
import { isShapeTextFrame } from '@/lib/shapes/shape-text-frame'
import { shapeAffectsTextBox } from '@/lib/text/wrap-obstacles'

/**
 * The text frames a wrap-source change may have to re-save: unlinked frames that respect text wrap.
 *
 * A frame's saved `<TL>`/`<TR>` lines are baked against the wrap context at the moment the frame
 * itself is saved, and yb360 paints those lines as they are — it never lays text out again. Linked
 * frames are re-saved by `redistributeChainStory` (see `chainsAffectedByWrapSourceChange`), so they
 * are left out here; nothing else re-saved an unlinked frame, which is why wrapping a triangle over
 * a text box wrapped the text in Larry but not in yb360.
 *
 * Computed once per commit and shared across every changed shape in it, so a batch commit checks
 * each shape against these few frames rather than against the whole spread.
 */
export function wrapTargetTextFrames(
  allShapes: readonly ShapeData[],
  chains: readonly TextChain[]
): ShapeData[] {
  const chainedUids = new Set(chains.flatMap((chain) => chain.textBox.map((tb) => tb.uid)))

  return allShapes.filter(
    (shape) =>
      shape.ignoreTextWrap !== true &&
      isShapeTextFrame(shape) &&
      (shape.uid == null || !chainedUids.has(String(shape.uid)))
  )
}

/**
 * Which of `candidates` (see `wrapTargetTextFrames`) one wrap-source shape's change affects.
 *
 * Checked against both the before and after state: turning wrap off, or moving the shape away,
 * changes the frame's layout as much as turning it on. `shapeAffectsTextBox` already returns false
 * for a shape with no active wrap style.
 */
export function textFramesAffectedByWrapSourceChange(
  beforeShape: ShapeData,
  afterShape: ShapeData,
  candidates: readonly ShapeData[],
  zIndexOf: (id: string) => number | undefined
): ShapeData[] {
  return candidates.filter(
    (frame) =>
      frame.id !== afterShape.id &&
      (shapeAffectsTextBox(afterShape, frame, zIndexOf) ||
        shapeAffectsTextBox(beforeShape, frame, zIndexOf))
  )
}
